import { useEffect, useState } from 'react';
import {
  Descriptions,
  notification,
  Form,
  Space,
  Row,
  Col,
  Input,
  Typography,
  Card,
  Skeleton,
  Radio,
  InputNumber,
} from 'antd';

import MESSAGES from '../../../enums/Messages';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  get as getModificationBudget,
  patch as updateBudget,
} from '../../../api/module/mass_ctm_budget';
import '../Form/style.scss';
import ContractorsModal from '../MassForm/ContractorsModal';
import { catchException } from '../../../utils/helpers/helpers';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';

const { Title } = Typography;

const MassModificationBudget = ({ sendPerm }) => {
  const navigate = useHistory();
  var { modification_id } = useParams();

  const [loadingSend, setLoadingSend] = useState(false);
  const [dataInfo, setDataInfo] = useState();
  const [dataModification, setDataModification] = useState();

  const [formModification] = Form.useForm();

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${dataInfo?.proyecto_id}/${modification_id}`,
    },

    {
      text: 'Modificacion modulo presupuestal',
    },
  ];

  useEffect(() => {
    async function fetchData() {
      setDataInfo();
      setDataModification();
      try {
        if (modification_id) {
          const response = await getModificationBudget(modification_id);
          setDataInfo(response.data.info);
          setDataModification({
            ...(response.data.modification || {}),
            clausula: response.data.clausula || '',
            justificacion: response.data.justificacion || '',
          });
        }
      } catch (err) {
        catchException(err);
      }
    }

    fetchData();
  }, [modification_id]);

  function formatCurrency(number) {
    number = number || 0;
    const formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(number).replace(/\./g, ',').replace(/,/g, '.');
  }

  const validateOperation = (value, tipo_operacion) => {
    if (value <= 0) {
      return Promise.reject(new Error('Debe ser un valor positivo'));
    }
    if (tipo_operacion === 'disminucion') {
      if (value > dataInfo.valor_disponible) {
        return Promise.reject(
          new Error('Debe ser igual o menor que el disponible'),
        );
      }
    }
    return Promise.resolve();
  };

  const sendFormModification = async (values) => {
    setLoadingSend(true);
    try {
      await updateBudget(modification_id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Módulo actualizado correctamente.',
        duration: 2,
      });
      setLoadingSend(false);
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingSend(false);
      catchException(error, MESSAGES.DESC_ERROR_POST_MSG);
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL PRESUPUESTAL
        </Title>
      </div>
      {dataInfo && dataModification ? (
        <Row align="middle" gutter={8}>
          <Col flex="auto">
            <Space direction={'vertical'} style={{ width: '100%' }}>
              <Descriptions
                layout="horizontal"
                column={1}
                bordered
                styles={{
                  label: { maxWidth: '8rem' },
                }}
                className="fade-in"
              >
                <Descriptions.Item label="Cargo / Rol">
                  <Row align="middle">
                    <Col flex="auto">{dataInfo.cargo_nombre}</Col>
                    <Col flex="0 0">
                      <ContractorsModal initialValues={dataInfo} />
                    </Col>
                  </Row>
                </Descriptions.Item>
                <Descriptions.Item label="Identificador interno de la modificación masiva">
                  {dataInfo.identificador_masivo}
                </Descriptions.Item>
              </Descriptions>
            </Space>

            <Form
              form={formModification}
              name="form_modificacion"
              layout="vertical"
              autoComplete="off"
              onFinish={sendFormModification}
              initialValues={dataModification}
              onValuesChange={(cv, v) => {
                if ('tipo_operacion' in cv || 'valor_operacion' in cv) {
                  if (!v.tipo_operacion || !v.valor_operacion) {
                    formModification.setFieldsValue({
                      valor_total_modificado: null,
                      valor_total_modificado_trp: null,
                    });
                    return;
                  }
                  const newValue =
                    v.tipo_operacion === 'aumento'
                      ? dataInfo.valor_total_vigente + v.valor_operacion
                      : dataInfo.valor_total_vigente - v.valor_operacion;

                  formModification.setFieldsValue({
                    valor_total_modificado: newValue,
                    valor_total_modificado_trp:
                      newValue * dataInfo.proyecto_trp,
                  });
                }
              }}
            >
              <Space direction={'vertical'} style={{ width: '100%' }}>
                <Card
                  size={'small'}
                  bordered={false}
                  title={<Title level={3}>4. Ajuste presupuestal</Title>}
                  style={{ padding: 0 }}
                  headStyle={{ padding: 0 }}
                  bodyStyle={{ padding: 0 }}
                >
                  {sendPerm && (
                    <Form.Item
                      label="Digite en letras el número de la cláusula de los contratos a modificar"
                      name="clausula"
                      rules={[
                        {
                          required: true,
                          message: 'Ingrese el nombre de una cláusula',
                        },
                        {
                          max: 100,
                          message:
                            'El campo debe tener un máximo de 100 caracteres',
                        },
                      ]}
                    >
                      <Input allowClear />
                    </Form.Item>
                  )}
                  <Descriptions
                    layout="horizontal"
                    column={1}
                    bordered
                    size="small"
                    className="fade-in"
                  >
                    <Descriptions.Item
                      label={
                        <div>
                          Valor total
                          <b style={{ color: 'rgb(51, 51, 255)' }}> VIGENTE</b>
                          (incluye impuestos , tasas y contribuciones)
                        </div>
                      }
                      style={{ maxWidth: '100px' }}
                    >
                      {formatCurrency(dataInfo.valor_total_vigente)}{' '}
                      {dataInfo.moneda_descripcion}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <div>
                          Valor total equivalente en COP
                          <b style={{ color: 'rgb(51, 51, 255)' }}> VIGENTE</b>
                        </div>
                      }
                      style={{ maxWidth: '100px' }}
                    >
                      {formatCurrency(dataInfo.valor_total_trp_vigente)} pesos
                      colombianos
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Space>
              <Form.Item
                name="tipo_operacion"
                label="Por favor seleccione el tipo de modificación a realizar:"
                style={{ marginTop: '20px' }}
                rules={[
                  {
                    required: true,
                    message: 'Debe seleccionar el tipo de operación',
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="aumento">Aumento</Radio>
                  <Radio value="disminucion">Disminución</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item dependencies={['tipo_operacion']} noStyle>
                {({ getFieldValue }) => {
                  const tipo_operacion = getFieldValue('tipo_operacion');
                  if (!tipo_operacion) return null;
                  return (
                    <Descriptions
                      layout="horizontal"
                      column={1}
                      bordered
                      size="small"
                      className="fade-in"
                    >
                      {tipo_operacion === 'disminucion' && (
                        <Descriptions.Item
                          label="Valor mínimo disponible por pagar de los contratos antes de la modificación"
                          style={{ maxWidth: '100px' }}
                        >
                          {formatCurrency(dataInfo.valor_disponible)}{' '}
                          {dataInfo.moneda_descripcion}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item
                        label={`Digite el valor a ${
                          tipo_operacion === 'aumento'
                            ? 'adicionar'
                            : 'disminuir'
                        }`}
                        style={{ maxWidth: '100px' }}
                      >
                        <Row gutter={10}>
                          <Col flex="auto">
                            <Form.Item
                              style={{ marginBottom: '0px' }}
                              name="valor_operacion"
                              label={null}
                              rules={[
                                {
                                  required: true,
                                  message: 'Ingrese un valor',
                                },
                                {
                                  validator: (_, v) =>
                                    validateOperation(v, tipo_operacion),
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="000..."
                                style={{ width: '100%' }}
                                formatter={(value) =>
                                  `$ ${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ',',
                                  )
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, '')
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="10em" style={{ paddingTop: '4px' }}>
                            {dataInfo.moneda_descripcion}
                          </Col>
                        </Row>
                      </Descriptions.Item>
                    </Descriptions>
                  );
                }}
              </Form.Item>
              <Descriptions
                layout="horizontal"
                column={1}
                bordered
                size="small"
                className="fade-in"
              >
                <Descriptions.Item
                  label={
                    <div>
                      Valor total contrato
                      <b style={{ color: 'rgb(0, 153, 0)' }}>
                        {' '}
                        MODIFICADO
                      </b>{' '}
                      (incluye impuestos,tasas y contribuciones)
                    </div>
                  }
                  style={{ maxWidth: '100px' }}
                >
                  <Row gutter={10}>
                    <Col flex="auto">
                      <Form.Item
                        style={{ marginBottom: '0px' }}
                        name="valor_total_modificado"
                        label={null}
                        dependencies={['tipo_operacion', 'valor_operacion']}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `$ ${parseFloat(value || 0).toFixed(2)}`.replace(
                              /\B(?=(\d{3})+(?!\d))/g,
                              ',',
                            )
                          }
                          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="10em" style={{ paddingTop: '4px' }}>
                      {dataInfo.moneda_descripcion}
                    </Col>
                  </Row>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div>
                      Valor total equivalente del contrato en COP
                      <b style={{ color: 'rgb(0, 153, 0)' }}> MODIFICADO</b>
                    </div>
                  }
                  style={{ maxWidth: '100px' }}
                >
                  <Row gutter={10}>
                    <Col flex="auto">
                      <Form.Item
                        style={{ marginBottom: '0px' }}
                        name="valor_total_modificado_trp"
                        label={null}
                        dependencies={['tipo_operacion', 'valor_operacion']}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `$ ${parseFloat(value || 0).toFixed(2)}`.replace(
                              /\B(?=(\d{3})+(?!\d))/g,
                              ',',
                            )
                          }
                          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="10em" style={{ paddingTop: '4px' }}>
                      pesos colombianos
                    </Col>
                  </Row>
                </Descriptions.Item>
              </Descriptions>

              <Form.Item
                label="Justificación"
                name="justificacion"
                rules={[
                  {
                    required: true,
                    message: 'Ingresa la Justificación',
                  },
                ]}
                style={{ marginTop: '20px' }}
              >
                <Input.TextArea allowClear />
              </Form.Item>

              <Space>
                <CustomButton
                  htmlType="button"
                  className={'back-button'}
                  type="default"
                  text={
                    <Link
                      to={`${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${dataInfo.proyecto_id}/${modification_id}`}
                    >
                      Volver
                    </Link>
                  }
                />

                <ModificationModuleSaveButton
                  onConfirm={() => formModification.submit()}
                  loading={loadingSend}
                  title={
                    <>
                      <p>
                        Esta modificación implica obligatoriamente cambiar el
                        módulo de forma de pago.
                      </p>
                      <p>Por favor seleccionelo para ajustarlo.</p>
                    </>
                  }
                />
              </Space>
            </Form>
          </Col>
        </Row>
      ) : (
        <Skeleton active loading />
      )}
    </>
  );
};

export default MassModificationBudget;
